/**
 * Audit › Trends (docs/CONTRACT-fleet-governance.md sections 7 to 10 and 12.3): the wire types of the eight sections,
 * written from the contract and never imported from the service; the calls to /v1/admin/governance/*; the range and app
 * kept in the address (`?days=30&app=` or `?from=&to=`); the words people read; the "Show audit rows" query builder; the
 * exports; the settings form; the governance copy's status and erase; and the probe that offers the page only where the
 * service has the `governance-trends` flag on (every route but the copy's two answers 404 `governance_trends_disabled`).
 *
 * The section answers carry counts only, never an actor (contract 1.5). The drill-down's rows are the audit ledger's own
 * rows and carry actors, as the Audit log shows them to the same two roles.
 */
import { useEffect, useState } from 'react';
import { errorText } from './errors';
import { adminGet, adminSend, queryOf, statusOf } from './fleetHttp';

// ------------------------------------------------------------------------------------------------ closed lists (section 7.7)

export const RISK_TIER_KEYS = ['standard', 'sensitive', 'financial', 'security', 'break-glass', 'none'] as const;
export type RiskTierKey = (typeof RISK_TIER_KEYS)[number];
export const REFUSAL_KINDS = ['risk', 'tool-setting', 'denied', 'rejected', 'cap', 'label-policy'] as const;
export type RefusalKind = (typeof REFUSAL_KINDS)[number];
export const TRENDS_SECTIONS = ['dlp', 'refusals', 'decisions', 'funnel', 'receivers', 'elevation', 'changes'] as const;
export type TrendsSection = (typeof TRENDS_SECTIONS)[number];

// ------------------------------------------------------------------------------------------------ the answers (section 9.1)

/** `range`: the dates the answer covers, both included; `previous`: the range of the same length just before. */
export interface Ranged {
  range: { from: string; to: string; days: number };
  previous: { from: string; to: string };
}

export interface Tile { value: number | null; previous: number | null; changePct: number | null }

export interface GovTiles extends Ranged {
  dlpBlocks: Tile;
  refusals: Tile;
  autoRunSharePct: Tile;
  awaitingNow: number;
  medianApproveSeconds: Tile;
  bridgeRefusals: Tile;
  elevationsGranted: Tile;
  elevationsDenied: Tile;
}

export interface GovDlpRow { kind: 'egress' | 'write'; app: string; detector: string; blocked: number; redacted: number; audited: number; hits: number; spark: number[] }
export interface GovDlpAnswer extends Ranged { items: GovDlpRow[]; next: string | null; total: number; unparsed: number; truncated: number }

export interface GovRefusalTier {
  riskTier: RiskTierKey; total: number;
  byKind: Record<RefusalKind, number>; spark: number[];
  topTools: Array<{ app: string; tool: string; kind: RefusalKind; count: number }>;
}
export interface GovRefusalsAnswer extends Ranged { tiers: GovRefusalTier[] }

export interface GovDecisionRow {
  app: string; autoRun: number;
  confirmed: number;
  approved: number; cancelled: number; rejected: number;
  expired: number;
  undecidedToday: number; toolSettingEnded: number; total: number;
  autoRunSpark: Array<number | null>;
}
export interface GovDecisionsAnswer extends Ranged { items: GovDecisionRow[] }

export interface GovFunnelRow {
  app: string; tool: string; proposed: number; confirmed: number; awaiting: number; approved: number; rejected: number;
  cancelled: number; expired: number; expiredPct: number | null;
  personP50s: number | null; personP95s: number | null; approverP50s: number | null; approverP95s: number | null;
}
export interface GovFunnelAnswer extends Ranged { items: GovFunnelRow[]; next: string | null; total: number }

export interface GovReceiverRow {
  receiver: string; calls: number; failed: number; refused: number; contract: number;
  reasons: Record<string, number>; rules: Record<string, number>; p95Ms: number | null; spark: number[];
}
export interface GovLabRow { app: string; filed: number; refused: number }
export interface GovReceiversAnswer extends Ranged { items: GovReceiverRow[]; bridgeAudit: Record<string, number>; lab: GovLabRow[] }

export interface GovElevation extends Ranged {
  granted: number; denied: number;
  days: Array<{ day: string; granted: number; denied: number; operators: number | null }>;
  deniedReasons: Array<{ reason: string; count: number }>;
}

export interface GovEventRow { event: string; count: number; spark: number[] }
export interface GovChangesAnswer extends Ranged { admin: GovEventRow[]; custody: GovEventRow[] }

/** One audit ledger row exactly (audit.ts AuditRecord): what the drill-down answers. */
export interface GovAuditRow {
  seq: number; ts: string; prevHash: string; hash: string;
  actor: { id: string; email?: string; trust?: string };
  app?: string; event: string; tool?: string; riskTier?: string; inputDigest?: string; outcome?: string; detail?: string;
  traceId?: string; eventId?: string;
}

export interface GovRowsAnswer {
  source: 'ledger' | 'copy' | 'ledger+copy' | 'rolled-off';
  note: string | null;
  ledgerFrom: string | null;
  copyFrom: string | null;
  items: GovAuditRow[];
  next: { beforeSeq: number } | null;
}

// ------------------------------------------------------------------------------------------------ settings and the copy (8.5, 10)

export interface GovSettings { rowsRetentionDays: number }
export const GOV_SETTINGS_DEFAULTS: GovSettings = { rowsRetentionDays: 180 };

/**
 * The last catch-up (`gov:meta backfill`, section 7.8). The contract names the record and its audit detail
 * (`fromSeq=1; throughSeq=944; rows=944; copied=611`) but not the route's field names: every field is read if present.
 */
export interface GovBackfill { at?: number | null; startedAt?: number | null; finishedAt?: number | null; fromSeq?: number; throughSeq?: number; rows?: number; copied?: number; state?: string }

export interface GovSettingsView {
  settings: GovSettings;
  defaults: GovSettings;
  flag: true;
  copy: { rows: number; oldestDay: string | null; newestDay: string | null };
  cursor: number;
  backfill: GovBackfill | null;
  jobs: Record<string, { lastRunAt: number; lastResult: string; lastError?: string }>;
}

export interface GovRowsStatus {
  trendsOn: boolean; rows: number; partitions: number; oldestDay: string | null; newestDay: string | null; retentionDays: number; canErase: boolean;
}

// ------------------------------------------------------------------------------------------------ the range and app (12.3)

export type RangeDays = 7 | 30 | 90;
export const RANGE_DAYS: readonly RangeDays[] = [7, 30, 90];
export const DEFAULT_DAYS: RangeDays = 30;

/** The page header's filter, kept in the address: `?days=30&app=` or `?from=&to=` (a custom range). */
export interface TrendsFilter { days: RangeDays | null; from: string | null; to: string | null; app: string }

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The filter a location carries; anything unreadable is the 30-day default. */
export function filterFromSearch(search: string): TrendsFilter {
  const params = new URLSearchParams(search);
  const app = (params.get('app') ?? '').trim();
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && DATE.test(from) && DATE.test(to)) return { days: null, from, to, app };
  const days = Number(params.get('days'));
  return { days: (RANGE_DAYS as readonly number[]).includes(days) ? (days as RangeDays) : DEFAULT_DAYS, from: null, to: null, app };
}

/** The range and app as every section route takes them: `days`, or `from` and `to`; `app` only when one is picked. */
export function rangeQuery(filter: TrendsFilter): { days?: number; from?: string; to?: string; app?: string } {
  const app = filter.app || undefined;
  if (filter.days === null && filter.from && filter.to) return { from: filter.from, to: filter.to, app };
  return { days: filter.days ?? DEFAULT_DAYS, app };
}

/** The page's address for a filter (the view's other parameters are not Trends'). */
export function filterSearch(filter: TrendsFilter): string {
  const query = queryOf({ ...(filter.days === null ? { from: filter.from, to: filter.to } : { days: filter.days }), app: filter.app });
  return query;
}

/** "the previous 30 days" for the tiles' change line. */
export function previousName(days: number): string {
  return `the previous ${days} day${days === 1 ? '' : 's'}`;
}

/** `+12 % on the previous 30 days`; a plain line when there is nothing earlier to compare with. */
export function changeText(tile: Tile, days: number): string {
  if (tile.changePct === null || !Number.isFinite(tile.changePct)) return `nothing in ${previousName(days)} to compare`;
  const rounded = Math.round(tile.changePct);
  return `${rounded > 0 ? '+' : ''}${rounded} % on ${previousName(days)}`;
}

// ------------------------------------------------------------------------------------------------ words people read

export const REFUSAL_KIND_LABELS: Record<RefusalKind, string> = {
  risk: 'Blocked by risk',
  'tool-setting': 'Blocked by a tool setting',
  denied: 'Denied',
  rejected: 'Rejected by an approver',
  cap: 'Cap',
  'label-policy': 'Label policy',
};

export const DLP_KIND_LABELS: Record<GovDlpRow['kind'], string> = { egress: 'Outbound', write: 'Tool write' };

/** A detector as people read it: an admin's deny-list term is never shown (the rollup holds `deny`). */
export function detectorLabel(detector: string): string {
  if (detector === 'deny') return 'Deny list term';
  if (detector === 'other') return 'Other';
  return detector;
}

/** `42 s`, `3 min`, `1 h 5 min`; an em dash for nothing. Rounded to whole minutes first, so never `60 min` or `1 h 60 min`. */
export function durationText(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 59.5) return `${Math.round(seconds)} s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
}

/** `5.2 %` from a 0 to 100 figure; an em dash for nothing. */
export function pctText(value: number | null | undefined, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)} %`;
}

/** A share of a whole, 0 to 1 (0 when the whole is 0). */
export function shareOf(part: number, whole: number): number {
  return whole > 0 ? part / whole : 0;
}

/** Sparkline values: a day without data draws as an empty bar. */
export function sparkValues(values: ReadonlyArray<number | null> | null | undefined): number[] {
  return (values ?? []).map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0));
}

/** A count table (`{ refused: 3, connector_contract: 1 }`) as rows, largest first, then by name. */
export function countRows(counts: Record<string, number> | null | undefined): Array<{ key: string; count: number }> {
  return Object.entries(counts ?? {}).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

// ------------------------------------------------------------------------------------------------ "Show audit rows" (section 9.3)

/** One way to read a number's rows: an exact event or an event prefix (the route takes exactly one). */
export interface DrillChoice { label: string; event?: string; eventPrefix?: string }

/** What a number opens: its events, its app and tool when it has them, and its range (or one day of it). */
export interface DrillRequest {
  title: string;
  choices: DrillChoice[];
  app?: string | null;
  tool?: string | null;
  /** The section answer's range, both dates included. */
  range: { from: string; to: string };
  /** One day of the range (a sparkline point, a day row), or null for the whole range. */
  day?: string | null;
}

/** The ledger events behind each refusal kind; `risk` and `denied` are counted from the turn traces and have none. */
export const REFUSAL_KIND_EVENTS: Record<RefusalKind, DrillChoice[]> = {
  risk: [],
  'tool-setting': [{ label: 'Blocked by a tool setting', event: 'tool.blocked' }],
  denied: [],
  rejected: [{ label: 'Rejected by an approver', event: 'action.rejected' }],
  cap: [{ label: 'Fleet cap', event: 'spend.cap_blocked' }, { label: 'Personal cap', event: 'user_spend.cap_blocked' }],
  'label-policy': [{ label: 'Label blocked', event: 'm365.label_blocked' }, { label: 'Label withheld', event: 'm365.label_withheld' }],
};

export const DLP_CHOICES: Record<GovDlpRow['kind'], DrillChoice> = {
  egress: { label: 'Outbound DLP', eventPrefix: 'egress.dlp_' },
  write: { label: 'Tool write DLP', eventPrefix: 'dlp.write_' },
};

export const ACTION_CHOICE: DrillChoice = { label: 'Approval cards and actions', eventPrefix: 'action.' };
export const AUTO_RUN_CHOICE: DrillChoice = { label: 'Ran on its own', event: 'action.auto_executed' };
export const ELEVATION_CHOICES: DrillChoice[] = [{ label: 'Granted', event: 'elevation.granted' }, { label: 'Denied', event: 'elevation.denied' }];

/** The drill-down window's longest span (section 9.3: `from` and `to` at most 31 days apart). */
export const DRILL_MAX_DAYS = 31;

/** `2026-10-24` plus `n` days, in UTC. */
export function addDays(day: string, n: number): string {
  const at = Date.parse(`${day}T00:00:00Z`) + n * 86_400_000;
  return new Date(at).toISOString().slice(0, 10);
}

/** The days of a range, oldest first (the drawer's day choice; a sparkline's points). */
export function daysOf(range: { from: string; to: string }): string[] {
  const days: string[] = [];
  if (!DATE.test(range.from) || !DATE.test(range.to)) return days;
  for (let day = range.from; day <= range.to && days.length < 800; day = addDays(day, 1)) days.push(day);
  return days;
}

/**
 * The drill-down's query for a request, the choice shown and the day picked: `event` or `eventPrefix`, `app`, `tool`, and
 * `from` (included) and `to` (the day after the last, excluded: the ledger keeps `from <= ts < to`). A range longer than
 * 31 days reads its last 31 days, and says so through `clamped`.
 */
export function drillQuery(request: DrillRequest, choice: DrillChoice, day: string | null, beforeSeq?: number): {
  query: Record<string, string | number | undefined>; from: string; to: string; clamped: boolean;
} {
  const first = day ?? request.range.from;
  const last = day ?? request.range.to;
  const to = addDays(last, 1);
  let from = first;
  let clamped = false;
  if (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`) > DRILL_MAX_DAYS * 86_400_000) {
    from = addDays(to, -DRILL_MAX_DAYS);
    clamped = true;
  }
  return {
    query: {
      ...(choice.event ? { event: choice.event } : { eventPrefix: choice.eventPrefix }),
      app: request.app || undefined,
      tool: request.tool || undefined,
      from,
      to,
      beforeSeq,
      limit: 100,
    },
    from,
    to,
    clamped,
  };
}

/** The governance copy's export for the same filters (it takes an exact event, not a prefix: section 9.4). */
export function rowsExportHref(filters: { from: string; to: string; event?: string; app?: string | null }): string {
  const params = new URLSearchParams({ format: 'ndjson', from: filters.from, to: filters.to });
  if (filters.event) params.set('event', filters.event);
  if (filters.app) params.set('app', filters.app);
  return `/v1/admin/governance/rows/export?${params.toString()}`;
}

/** A section as NDJSON with the range and app (section 9.4); counts only. */
export function trendsExportHref(section: TrendsSection, filter: TrendsFilter): string {
  const params = new URLSearchParams({ section });
  for (const [key, value] of Object.entries(rangeQuery(filter))) if (value !== undefined && value !== '') params.set(key, String(value));
  return `/v1/admin/governance/export?${params.toString()}`;
}

// ------------------------------------------------------------------------------------------------ calls

type Query = Record<string, string | number | boolean | null | undefined>;

export const trendsApi = {
  settings: () => adminGet<GovSettingsView>('/v1/admin/governance/settings'),
  saveSettings: (patch: Partial<GovSettings>) => adminSend<GovSettingsView>('/v1/admin/governance/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  tiles: (query: Query) => adminGet<GovTiles>(`/v1/admin/governance/tiles${queryOf(query)}`),
  dlp: (query: Query) => adminGet<GovDlpAnswer>(`/v1/admin/governance/dlp${queryOf(query)}`),
  refusals: (query: Query) => adminGet<GovRefusalsAnswer>(`/v1/admin/governance/refusals${queryOf(query)}`),
  decisions: (query: Query) => adminGet<GovDecisionsAnswer>(`/v1/admin/governance/decisions${queryOf(query)}`),
  funnel: (query: Query) => adminGet<GovFunnelAnswer>(`/v1/admin/governance/funnel${queryOf(query)}`),
  receivers: (query: Query) => adminGet<GovReceiversAnswer>(`/v1/admin/governance/receivers${queryOf(query)}`),
  elevation: (query: Query) => adminGet<GovElevation>(`/v1/admin/governance/elevation${queryOf(query)}`),
  changes: (query: Query) => adminGet<GovChangesAnswer>(`/v1/admin/governance/changes${queryOf(query)}`),
  rows: (query: Query) => adminGet<GovRowsAnswer>(`/v1/admin/governance/rows${queryOf(query)}`),
  rowsStatus: () => adminGet<GovRowsStatus>('/v1/admin/governance/rows/status'),
  eraseRows: (confirmRows: number) => adminSend<{ erased: number; partitions: number }>('/v1/admin/governance/rows/erase', { method: 'POST', body: JSON.stringify({ confirmRows }) }),
};

// ------------------------------------------------------------------------------------------------ the settings form (section 10)

export const RETENTION_HELP = 'The governance copy keeps the audit rows Trends counts, so Show audit rows still reaches a day after the audit log has moved past it. 180 days unless changed; at most 365.';

/** The first thing the service would refuse, in words, or null. */
export function validateRetention(text: string): string | null {
  const value = Number(text.trim());
  if (!text.trim() || !Number.isInteger(value) || value < 1 || value > 365) return 'Keep the governance copy for (days): a whole number from 1 to 365.';
  return null;
}

/**
 * The last catch-up in words, from whatever fields the service sends (GovBackfill). The service always sends the object, with
 * `state` `not-run`, `running` or `done`; the state is put in words, never shown as it comes.
 */
export function backfillText(backfill: GovBackfill | null | undefined, when: (at: number) => string): string {
  const running = backfill?.state === 'running';
  const parts: string[] = [];
  if (backfill) {
    const at = backfill.finishedAt ?? backfill.at ?? backfill.startedAt ?? null;
    if (typeof at === 'number') parts.push(when(at));
    if (typeof backfill.rows === 'number') parts.push(`${backfill.rows.toLocaleString('en-US')} rows counted`);
    if (typeof backfill.copied === 'number') parts.push(`${backfill.copied.toLocaleString('en-US')} copied`);
    if (typeof backfill.fromSeq === 'number' && typeof backfill.throughSeq === 'number') parts.push(`ledger rows ${backfill.fromSeq.toLocaleString('en-US')} to ${backfill.throughSeq.toLocaleString('en-US')}`);
  }
  if (!parts.length) return running ? 'The first catch-up is running now.' : backfill?.state === 'done' ? 'A catch-up ran.' : 'No catch-up has run yet.';
  if (running) parts.push('another is running now');
  return parts.join(' · ');
}

// ------------------------------------------------------------------------------------------------ the copy card (12.4)

export function copyHoldsText(status: Pick<GovRowsStatus, 'rows' | 'oldestDay' | 'newestDay'>): string {
  const rows = `${status.rows.toLocaleString('en-US')} row${status.rows === 1 ? '' : 's'}`;
  return status.oldestDay && status.newestDay ? `The governance copy holds ${rows} from ${status.oldestDay} to ${status.newestDay}.` : `The governance copy holds ${rows}.`;
}

export function eraseAskText(rows: number): string {
  return `Erase ${rows.toLocaleString('en-US')} rows of the governance copy? The audit log and the Trends counts are not affected. This cannot be undone.`;
}

// ------------------------------------------------------------------------------------------------ the probe (section 12.1)

/** The probe's answer: a 404 (`governance_trends_disabled`), 401 or 403 hides the page; any other failure keeps it. */
export function trendsVisible(probeError: unknown): boolean {
  if (probeError === null || probeError === undefined) return true;
  const status = statusOf(probeError);
  return !(status === 404 || status === 401 || status === 403);
}

export function trendsNav(consoleRole: boolean, probe: boolean | null): 'checking' | 'shown' | 'hidden' {
  if (!consoleRole) return 'hidden';
  if (probe === null) return 'checking';
  return probe ? 'shown' : 'hidden';
}

/** GET /v1/admin/governance/settings, once per console session for an admin or auditor (the Pulse probe's pattern). */
export function useTrendsNav(consoleRole: boolean): 'checking' | 'shown' | 'hidden' {
  const [probe, setProbe] = useState<boolean | null>(null);
  useEffect(() => {
    if (!consoleRole) return;
    let live = true;
    setProbe(null);
    trendsApi.settings().then(
      () => { if (live) setProbe(true); },
      (error: unknown) => { if (live) setProbe(trendsVisible(error)); },
    );
    return () => {
      live = false;
    };
  }, [consoleRole]);
  return trendsNav(consoleRole, probe);
}

/** A failed read or save in words: the dark service, a refused write, storage that is down, else the service's own words. */
export function trendsFailure(error: unknown, what = 'The Trends data could not be read.'): string {
  const status = statusOf(error);
  const message = (error as { message?: unknown } | null)?.message;
  if (status === 404 && message === 'governance_trends_disabled') return 'Trends is switched off: the service answers 404 until the governance-trends flag is on under Feature flags.';
  if (status === 403) return 'Only IT admins can make this change.';
  if (status === 503) return 'Trends storage is unavailable just now. Try again in 30 seconds.';
  return errorText(error, what);
}
